import type { PayrollLineResult } from '@felix/domain';
import { Button, Modal, Select } from '@mantine/core';
import { IconChartBar, IconClock, IconUsers, IconWallet } from '@tabler/icons-react';
import { type ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { EmptyState } from '../../EmptyState.js';
import { formatDateTime } from '../../format.js';
import { PageHeader } from '../../PageHeader.js';
import { StatusBadge } from '../../StatusBadge.js';
import { pdfErrorKey, renderPdf } from '../personnel/pdf.js';
import { errorCode } from '../personnel/source.js';
import { LoadError, TableSkeleton } from '../purchasing/table.js';
import classes from './Payroll.module.css';
import {
  DeductionTypesDialog,
  ExtraTypesDialog,
  PayrollDialog,
  type PayrollDialogProps,
} from './PayrollDialogs.js';
import {
  AllotmentForm,
  EntryForm,
  ExtraForm,
  FORM_TITLE,
  LineForm,
  PaymentForm,
  PayrollModalContext,
  type SaveBody,
} from './PayrollForms.js';
import {
  AdvancesSection,
  AllotmentsSection,
  ClosingSection,
  MonthlySection,
  PaymentsSection,
  PayslipsSection,
  ReportsSection,
  type SectionProps,
} from './PayrollSections.js';
import { netPaymentListPdf, payrollPdfFileName, payslipPdf } from './payslip-pdf.js';
import type { PayrollSource } from './source.js';
import type {
  PayrollData,
  PayrollDocument,
  PayrollKind,
  PayrollOfficeRequest,
  PayrollPayment,
  PayrollSection,
} from './types.js';
import { formatMoney, monthLabel, monthOptions, netPaymentCsv, primaryProgress } from './view.js';

export interface PayrollScreenProps {
  source: PayrollSource;
  section: PayrollSection;
  /** Bugün (gemi yerel günü ya da ofiste UTC), `YYYY-MM-DD`; dönem seçicisi ve yeni dönem için. */
  today: string;
}

type State =
  | { kind: 'loading' }
  | { kind: 'error'; code: string }
  | { kind: 'ready'; data: PayrollData };

/** Açık kayıt; yeni extra için kişi önceden seçilebilir (ADR 0068 §5). */
type Selected = { kind: PayrollKind; id: string | null; userId?: string | undefined } | null;

/** Menü yaprağının yolu (ADR 0061 §7). */
export const PAYROLL_SECTION_PATHS: Record<PayrollSection, string> = {
  monthly: '/crew/payroll',
  advances: '/crew/payroll/advances',
  allotments: '/crew/payroll/allotments',
  payslips: '/crew/payroll/payslips',
  payments: '/crew/payroll/payments',
  closing: '/crew/payroll/closing',
  reports: '/crew/payroll/reports',
};

/**
 * Crew ▸ Payroll (M16; ADR 0061): ortağın "Crew Payroll — Payment Register & Reconciliation"
 * taslağının düzeni; başlıkta gemi, dönem ve durum, dört özet kartı, bölümün tablosu ve sağda
 * ayrıntı paneli. Bölüm menü yaprağından gelir. Hesabı gemide kaptan yazar, ofis salt okunur görür
 * ve dışa aktarır; ödeme, dekont ve mutabakatı ofis yazar, gemi salt okunur görür (ADR 0066 §1).
 * Ekran gemi uygulamasında ve web ofiste aynıdır (ADR 0019 §2).
 */
export function PayrollScreen({ source, section, today }: PayrollScreenProps) {
  const { t, i18n } = useTranslation();
  const language = i18n.resolvedLanguage ?? 'en';
  const [state, setState] = useState<State>({ kind: 'loading' });
  const [query, setQuery] = useState<{ vesselId?: string; month?: string }>({});
  const [selected, setSelected] = useState<Selected>(null);
  const [dialog, setDialog] = useState<Omit<PayrollDialogProps, 'onClose'> | null>(null);
  const [notice, setNotice] = useState<{ tone: 'success' | 'danger'; text: string } | null>(null);
  const [documents, setDocuments] = useState<PayrollDocument[] | null>(null);
  const [tab, setTab] = useState<'register' | 'reconciliation'>('register');
  const [typesOpen, setTypesOpen] = useState(false);
  const [deductionTypesOpen, setDeductionTypesOpen] = useState(false);
  // Bölüm değişince açık pencere kapanır (pencere menüden gelen bölüme taşınmaz)
  // biome-ignore lint/correctness/useExhaustiveDependencies: yalnız bölüm değişiminde
  useEffect(() => {
    setSelected(null);
    setTypesOpen(false);
    setDeductionTypesOpen(false);
  }, [section]);

  const load = useCallback(async () => {
    try {
      const data = await source.load(query);
      setState({ kind: 'ready', data });
    } catch (error) {
      setState({ kind: 'error', code: errorCode(error) });
    }
  }, [source, query]);
  useEffect(() => {
    setState({ kind: 'loading' });
    void load();
  }, [load]);

  const data = state.kind === 'ready' ? state.data : null;
  const editor = source.editor;
  const payer = source.payments;
  const can = {
    create: Boolean(editor && data?.can.create),
    edit: Boolean(editor && data?.can.edit),
    void: Boolean(editor && data?.can.void),
    approve: Boolean(editor && data?.can.approve),
    export: Boolean(data?.can.export),
    // Ödeme ofiste (ADR 0066 §1)
    payments: {
      create: Boolean(payer && data?.can.payments?.create),
      edit: Boolean(payer && data?.can.payments?.edit),
      void: Boolean(payer && data?.can.payments?.void),
    },
    // Şirketin extra türleri ofiste (ADR 0068 §2)
    extraTypes: {
      create: Boolean(source.extraTypes && data?.can.extraTypes?.create),
      edit: Boolean(source.extraTypes && data?.can.extraTypes?.edit),
      void: Boolean(source.extraTypes && data?.can.extraTypes?.void),
    },
    // Şirketin kesinti türleri ofiste; silinmez, pasiflenir (ADR 0080 §2)
    deductionTypes: {
      create: Boolean(source.deductionTypes && data?.can.deductionTypes?.create),
      edit: Boolean(source.deductionTypes && data?.can.deductionTypes?.edit),
    },
    // Ofisten dönem ve satır talebi; gemi sunucusu uygular (ADR 0165 §2)
    office: {
      create: Boolean(!editor && source.office && data?.can.office?.create),
      edit: Boolean(!editor && source.office && data?.can.office?.edit),
    },
  };
  const office = !editor ? source.office : undefined;

  // Ödeme panelinde dekontlar
  const paymentId = selected?.kind === 'payments' ? selected.id : null;
  useEffect(() => {
    setDocuments(null);
    if (!paymentId) return;
    source
      .documents(paymentId)
      .then(setDocuments)
      .catch(() => setDocuments([]));
  }, [paymentId, source]);

  const done = async (text: string) => {
    setNotice({ tone: 'success', text });
    await load();
  };
  const fail = (error: unknown) =>
    setNotice({
      tone: 'danger',
      text:
        t(`payroll.errors.codes.${errorCode(error)}`, { defaultValue: '' }) ||
        t('payroll.errors.save', { code: errorCode(error) }),
    });

  const save = async (kind: PayrollKind, body: SaveBody) => {
    // Ofiste satır gemiye talep olarak gider; satır gemide uygulanınca görünür (ADR 0165 §2)
    if (kind === 'lines' && office && data && body.periodId) {
      await office.saveLine({
        vesselId: data.vesselId,
        periodId: body.periodId,
        ...(body.id ? { lineId: body.id } : {}),
        ...(body.rowVersion !== undefined ? { rowVersion: body.rowVersion } : {}),
        input: body.input,
      });
      setSelected(null);
      await done(t('payroll.notices.requestSent'));
      return;
    }
    const saved =
      kind === 'payments'
        ? data && (await payer?.save({ ...body, vesselId: data.vesselId }))
        : await editor?.save(kind, body);
    if (!saved) return;
    setSelected({ kind, id: saved.id });
    await done(t('payroll.notices.saved'));
  };

  const remove = (kind: PayrollKind, id: string, rowVersion: number) =>
    setDialog({
      title: t('payroll.dialogs.removeTitle'),
      text: t('payroll.dialogs.removeText'),
      reason: { hint: t('payroll.dialogs.reasonHint'), max: 500 },
      confirmLabel: t('payroll.actions.remove'),
      onConfirm: async ({ reason }) => {
        if (kind === 'payments') await payer?.remove(id, rowVersion, reason);
        else await editor?.remove(kind, id, rowVersion, reason);
        setDialog(null);
        setSelected(null);
        await done(t('payroll.notices.removed'));
      },
    });

  const reconcile = (payment: PayrollPayment) =>
    setDialog({
      title: t('payroll.dialogs.reconcileTitle'),
      text: t('payroll.dialogs.reconcileText'),
      confirmLabel: t('payroll.actions.reconcile'),
      onConfirm: async () => {
        await payer?.reconcile(payment.id, payment.rowVersion);
        setDialog(null);
        await done(t('payroll.notices.reconciled'));
      },
    });

  const brand = async () => (source.brand ? await source.brand().catch(() => null) : null);
  const pdfCommon = async (d: PayrollData) => ({
    vessel: { name: d.vesselName },
    month: d.month,
    period: d.period,
    closingHash: d.events.find((e) => e.event === 'closed')?.contentHash ?? null,
    brand: await brand(),
    printedAt: new Date().toISOString(),
    language,
  });
  const tt = (key: string, options?: Record<string, unknown>) => t(key, options ?? {});
  const output = async (fileName: string, render: () => Promise<Uint8Array>) => {
    if (!source.pdf) return;
    try {
      await source.pdf.save(fileName, render);
    } catch (error) {
      setNotice({ tone: 'danger', text: t(pdfErrorKey(errorCode(error))) });
    }
  };
  const payslips = async (lines: readonly PayrollLineResult[]) => {
    if (!data) return;
    const common = await pdfCommon(data);
    const name = lines.length === 1 ? (lines[0]?.name ?? '') : data.vesselName;
    await output(payrollPdfFileName(t('payroll.pdf.payslip'), name, data.month), () =>
      renderPdf(payslipPdf(tt, { ...common, lines, payments: data.payments })),
    );
  };
  const netListPdf = async () => {
    if (!data) return;
    const common = await pdfCommon(data);
    await output(payrollPdfFileName(t('payroll.pdf.netList'), data.vesselName, data.month), () =>
      renderPdf(netPaymentListPdf(tt, { ...common, computation: data.computation })),
    );
  };
  const csv = async () => {
    if (!data) return;
    const text = `﻿${netPaymentCsv(data.computation, (key) => t(`payroll.csv.${key}`))}`;
    const fileName = `${payrollPdfFileName(t('payroll.pdf.netList'), data.vesselName, data.month).slice(0, -4)}.csv`;
    const bytes = new TextEncoder().encode(text);
    if (source.saveFile) {
      await source.saveFile(fileName, bytes, 'text/csv');
      return;
    }
    const url = URL.createObjectURL(new Blob([bytes], { type: 'text/csv;charset=utf-8' }));
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = fileName;
    anchor.click();
    setTimeout(() => URL.revokeObjectURL(url), 60_000);
  };

  const months = useMemo(
    () => monthOptions(data?.periods.map((p) => p.month) ?? [], today),
    [data, today],
  );

  const header = (
    <PageHeader
      title={t('payroll.title')}
      description={t(`payroll.sections.${section}`)}
      aside={
        <div className={classes.selectors}>
          <Select
            label={t('payroll.filters.vessel')}
            data={(
              data?.vessels ?? (data ? [{ id: data.vesselId, name: data.vesselName }] : [])
            ).map((v) => ({ value: v.id, label: v.name }))}
            value={data?.vesselId ?? null}
            readOnly={!data?.vessels}
            {...(data?.vessels ? {} : { rightSection: null })}
            allowDeselect={false}
            onChange={(value) => {
              setSelected(null);
              setQuery((q) => ({ ...q, ...(value ? { vesselId: value } : {}) }));
            }}
          />
          <Select
            label={t('payroll.filters.period')}
            data={months.map((m) => ({ value: m, label: monthLabel(m, language) }))}
            value={data?.month ?? null}
            allowDeselect={false}
            onChange={(value) => {
              setSelected(null);
              setQuery((q) => ({ ...q, ...(value ? { month: value } : {}) }));
            }}
          />
          <div
            className={classes.statusBox}
            data-status={data?.period?.status ?? 'none'}
            role="status"
            aria-label={t('payroll.filters.status')}
          >
            <StatusBadge
              tone={
                data?.period?.status === 'open'
                  ? 'success'
                  : data?.period?.status === 'closed'
                    ? 'neutral'
                    : 'warning'
              }
              variant="dot"
            >
              {t(`payroll.periodStatus.${data?.period?.status ?? 'none'}`)}
            </StatusBadge>
          </div>
        </div>
      }
    />
  );

  if (state.kind === 'error') {
    return (
      <div className={classes.screen}>
        {header}
        {state.code === 'FORBIDDEN' ? (
          <EmptyState tone="danger" title={t('payroll.empty.noAccess')} />
        ) : (
          <LoadError
            title={t('payroll.errors.load')}
            code={state.code}
            onRetry={() => void load()}
          />
        )}
      </div>
    );
  }
  if (!data) {
    return (
      <div className={classes.screen}>
        {header}
        <TableSkeleton columns={8} label={t('payroll.title')} />
      </div>
    );
  }

  const openPeriod = async () => {
    try {
      if (editor) {
        await editor.openPeriod(data.month);
        await done(t('payroll.notices.opened'));
      } else if (office) {
        await office.openPeriod(data.vesselId, data.month);
        await done(t('payroll.notices.requestSent'));
      }
    } catch (error) {
      fail(error);
    }
  };
  const requests = data.officeRequests ?? [];
  // Aynı ay için bekleyen açma talebi varken ikinci talep gönderilmez (`PERIOD_PENDING`)
  const openPending = requests.some((r) => r.kind === 'open_period' && r.status === 'pending');

  const sectionProps: SectionProps = {
    data,
    can,
    selected,
    onOpen: (kind, id, userId) => {
      setNotice(null);
      setSelected({ kind, id, userId });
    },
    onExtraTypes: source.extraTypes ? () => setTypesOpen(true) : undefined,
    onDeductionTypes: source.deductionTypes ? () => setDeductionTypesOpen(true) : undefined,
    onRemove: remove,
    onReconcile: reconcile,
    onPayslips: (lines) => void payslips(lines),
    onNetListPdf: () => void netListPdf(),
    onCsv: () => void csv(),
    onClose: () =>
      data.period &&
      setDialog({
        title: t('payroll.dialogs.closeTitle', { month: monthLabel(data.month, language) }),
        text: t('payroll.dialogs.closeText'),
        password: true,
        confirmLabel: t('payroll.actions.close'),
        onConfirm: async ({ password }) => {
          if (!data.period) return;
          await editor?.closePeriod(data.period.id, data.period.rowVersion, password);
          setDialog(null);
          await done(t('payroll.notices.closed'));
        },
      }),
    onReopen: () =>
      data.period &&
      setDialog({
        title: t('payroll.dialogs.reopenTitle', { month: monthLabel(data.month, language) }),
        text: t('payroll.dialogs.reopenText'),
        reason: { hint: t('payroll.dialogs.reopenReasonHint'), max: 2000 },
        password: true,
        confirmLabel: t('payroll.actions.reopen'),
        onConfirm: async ({ reason, password }) => {
          if (!data.period) return;
          await editor?.reopenPeriod(data.period.id, data.period.rowVersion, reason, password);
          setDialog(null);
          await done(t('payroll.notices.reopened'));
        },
      }),
  };

  const open = data.period?.status === 'open';
  const panel = selected ? panelOf(selected) : null;
  function panelOf(current: NonNullable<Selected>): ReactNode {
    if (!data) return null;
    const close = () => setSelected(null);
    const key = `${current.kind}-${current.id ?? 'new'}-${data.period?.rowVersion ?? 0}`;
    if (current.kind === 'lines') {
      const line = data.computation.lines.find((l) => l.id === current.id) ?? null;
      const ref = data.lines.find((l) => l.id === current.id);
      return (
        <LineForm
          key={key}
          data={data}
          line={line}
          editable={
            office
              ? (line ? can.office.edit : can.office.create) && open
              : line
                ? can.edit && open
                : can.create && open
          }
          request={Boolean(office)}
          onSave={save}
          onCancel={close}
          onRemove={
            ref && can.void && open ? () => remove('lines', ref.id, ref.rowVersion) : undefined
          }
          onAddExtra={
            line && can.create && open
              ? () => setSelected({ kind: 'extras', id: null, userId: line.userId })
              : undefined
          }
          onOpenExtra={(id) => setSelected({ kind: 'extras', id })}
        />
      );
    }
    if (current.kind === 'extras') {
      const extra = (data.extras ?? []).find((x) => x.id === current.id);
      return (
        <ExtraForm
          key={`${key}-${extra?.rowVersion ?? 0}-${current.userId ?? ''}`}
          data={data}
          extraId={current.id}
          userId={current.userId}
          editable={extra ? can.edit && open : can.create && open}
          onSave={save}
          onCancel={close}
          onRemove={
            extra && can.void && open
              ? () => remove('extras', extra.id, extra.rowVersion)
              : undefined
          }
        />
      );
    }
    if (current.kind === 'entries') {
      const entry = data.entries.find((e) => e.id === current.id);
      return (
        <EntryForm
          key={key}
          data={data}
          entryId={current.id}
          editable={entry ? can.edit && open : can.create && open}
          onSave={save}
          onCancel={close}
          onRemove={
            entry && can.void && open
              ? () => remove('entries', entry.id, entry.rowVersion)
              : undefined
          }
        />
      );
    }
    if (current.kind === 'allotments') {
      const item = data.allotments.find((a) => a.id === current.id);
      return (
        <AllotmentForm
          key={key}
          data={data}
          allotmentId={current.id}
          editable={item ? can.edit : can.create}
          onSave={save}
          onCancel={close}
          onRemove={
            item && can.void ? () => remove('allotments', item.id, item.rowVersion) : undefined
          }
        />
      );
    }
    const payment = data.payments.find((p) => p.id === current.id);
    return (
      <PaymentForm
        key={`${key}-${payment?.rowVersion ?? 0}`}
        data={data}
        paymentId={current.id}
        editable={payment ? can.payments.edit : can.payments.create}
        onSave={save}
        onCancel={close}
        onRemove={
          payment && can.payments.void
            ? () => remove('payments', payment.id, payment.rowVersion)
            : undefined
        }
        onReconcile={payment && can.payments.edit ? () => reconcile(payment) : undefined}
        documents={documents}
        documentUrl={source.documentUrl ? (doc) => source.documentUrl?.(doc) : undefined}
        onUpload={
          payment && payer && can.payments.create
            ? async (file) => {
                await payer.upload(payment.id, file);
                setDocuments(await source.documents(payment.id));
                setNotice({
                  tone: 'success',
                  text: t('payroll.notices.uploaded', { name: file.name }),
                });
                await load();
              }
            : undefined
        }
      />
    );
  }

  const content = !data.period ? (
    <EmptyState
      title={t(editor ? 'payroll.empty.noPeriodShip' : 'payroll.empty.noPeriodWeb', {
        month: monthLabel(data.month, language),
      })}
      action={
        can.create && data.month <= today.slice(0, 7) ? (
          <Button onClick={() => void openPeriod()}>
            {t('payroll.actions.openPeriod', { month: monthLabel(data.month, language) })}
          </Button>
        ) : can.office.create && !openPending && data.month <= today.slice(0, 7) ? (
          <Button onClick={() => void openPeriod()}>
            {t('payroll.actions.requestPeriod', { month: monthLabel(data.month, language) })}
          </Button>
        ) : undefined
      }
    />
  ) : section === 'monthly' ? (
    <MonthlySection {...sectionProps} />
  ) : section === 'advances' ? (
    <AdvancesSection {...sectionProps} />
  ) : section === 'allotments' ? (
    <AllotmentsSection {...sectionProps} />
  ) : section === 'payslips' ? (
    <PayslipsSection {...sectionProps} />
  ) : section === 'payments' ? (
    <PaymentsSection {...sectionProps} tab={tab} onTab={setTab} />
  ) : section === 'closing' ? (
    <ClosingSection {...sectionProps} />
  ) : (
    <ReportsSection {...sectionProps} />
  );

  return (
    <div className={classes.screen}>
      {header}
      <SummaryCards data={data} />
      {notice ? (
        <p
          className={classes.notice}
          data-tone={notice.tone}
          role={notice.tone === 'danger' ? 'alert' : 'status'}
        >
          {notice.text}
        </p>
      ) : null}
      {data.period?.status === 'closed' && (section === 'monthly' || section === 'advances') ? (
        <p className={classes.note}>{t('payroll.form.closedNote')}</p>
      ) : null}
      {data.period && section === 'payments' && !payer ? (
        <p className={classes.note}>{t('payroll.form.paymentsOffice')}</p>
      ) : !editor && data.period && section !== 'payments' ? (
        <p className={classes.note}>
          {t(
            can.office.create || can.office.edit
              ? 'payroll.form.officeEdit'
              : 'payroll.form.readOnly',
          )}
        </p>
      ) : null}
      <div className={classes.body}>{content}</div>
      {requests.length > 0 ? <OfficeRequests requests={requests} /> : null}
      {/* Düzenleme ekranın ortasında açılan pencerede (ortağın isteği 2026-10-17; ADR 0068 §6) */}
      <Modal
        opened={panel !== null && selected !== null}
        onClose={() => setSelected(null)}
        title={selected ? t(`payroll.form.${FORM_TITLE[selected.kind]}`) : ''}
        centered
        size="lg"
        classNames={{ title: classes.panelTitle }}
        closeButtonProps={{ 'aria-label': t('payroll.actions.closeWindow') }}
      >
        <PayrollModalContext.Provider value>{panel}</PayrollModalContext.Provider>
      </Modal>
      {typesOpen && source.extraTypes ? (
        <ExtraTypesDialog
          types={data.extraTypes ?? []}
          editor={source.extraTypes}
          can={can.extraTypes}
          onChanged={load}
          onClose={() => setTypesOpen(false)}
        />
      ) : null}
      {deductionTypesOpen && source.deductionTypes ? (
        <DeductionTypesDialog
          types={data.deductionTypes ?? []}
          editor={source.deductionTypes}
          can={can.deductionTypes}
          onChanged={load}
          onClose={() => setDeductionTypesOpen(false)}
        />
      ) : null}
      {dialog ? <PayrollDialog {...dialog} onClose={() => setDialog(null)} /> : null}
    </div>
  );
}

/** Ofisin gemiye gönderdiği talepler ve durumları (ADR 0165 §2); reddedilenin gerekçesi. */
function OfficeRequests({ requests }: { requests: PayrollOfficeRequest[] }) {
  const { t, i18n } = useTranslation();
  const language = i18n.resolvedLanguage ?? 'en';
  const title = t('payroll.office.title');
  return (
    <section className={classes.requests} aria-label={title}>
      <h2 className={classes.label}>{title}</h2>
      <p className={classes.note}>{t('payroll.office.hint')}</p>
      <table className={classes.requestTable}>
        <thead>
          <tr>
            <th>{t('payroll.office.columns.request')}</th>
            <th>{t('payroll.office.columns.sent')}</th>
            <th>{t('payroll.office.columns.status')}</th>
          </tr>
        </thead>
        <tbody>
          {requests.map((r) => (
            <tr key={r.id}>
              <td>
                {r.kind === 'open_period'
                  ? t('payroll.office.kinds.open_period', {
                      month: monthLabel(r.periodMonth, language),
                    })
                  : t(
                      r.lineId && r.userName
                        ? 'payroll.office.kinds.save_line'
                        : 'payroll.office.kinds.add_line',
                      {
                        name: r.userName ?? '',
                      },
                    )}
              </td>
              <td>
                {formatDateTime(r.createdAt)}
                {r.createdByName ? ` · ${r.createdByName}` : ''}
              </td>
              <td>
                <StatusBadge
                  tone={
                    r.status === 'applied'
                      ? 'success'
                      : r.status === 'rejected'
                        ? 'danger'
                        : 'warning'
                  }
                >
                  {t(`payroll.office.status.${r.status}`)}
                </StatusBadge>
                {r.status === 'rejected' ? (
                  <span className={classes.cardNote}>
                    {t(`payroll.office.reasons.${r.reasonCode ?? ''}`, {
                      defaultValue: r.reason ?? r.reasonCode ?? '',
                    })}
                  </span>
                ) : null}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  );
}

/** Dört özet kartı (ortağın görüntüsündeki sıra): ödenecek, kaydedilen, kalan, ilerleme. */
function SummaryCards({ data }: { data: PayrollData }) {
  const { t, i18n } = useTranslation();
  const language = i18n.resolvedLanguage ?? 'en';
  const money = (minor: number) => formatMoney(minor, language);
  const progress = primaryProgress(data.progress);
  const main = progress?.main;
  const currency = main?.currency ?? 'USD';
  const others = progress?.others ?? [];
  const otherText = (pick: (r: (typeof others)[number]) => number) =>
    others.length > 0
      ? t('payroll.cards.others', {
          list: others.map((r) => `${r.currency} ${money(pick(r))}`).join(', '),
        })
      : null;
  const card = (
    label: string,
    value: string,
    icon: ReactNode,
    note: string | null,
    extra?: ReactNode,
  ) => (
    <section className={classes.card} aria-label={label}>
      <div>
        <span className={classes.cardLabel}>{label}</span>
        <span className={classes.cardValue}>{value}</span>
        {note ? <span className={classes.cardNote}>{note}</span> : null}
      </div>
      <span className={classes.cardIcon} aria-hidden>
        {icon}
      </span>
      {extra}
    </section>
  );
  const percent = Math.round((main?.progress ?? 0) * 1000) / 10;
  return (
    <div className={classes.cards}>
      {card(
        t('payroll.cards.payable', { currency }),
        money(main?.payable ?? 0),
        <IconUsers size={22} />,
        otherText((r) => r.payable),
      )}
      {card(
        t('payroll.cards.recorded', { currency }),
        money(main?.recorded ?? 0),
        <IconWallet size={22} />,
        otherText((r) => r.recorded),
      )}
      {card(
        t('payroll.cards.outstanding', { currency }),
        money(main?.outstanding ?? 0),
        <IconClock size={22} />,
        otherText((r) => r.outstanding),
      )}
      {card(
        t('payroll.cards.progress'),
        `${new Intl.NumberFormat(language.startsWith('tr') ? 'tr-TR' : 'en-US', { maximumFractionDigits: 1 }).format(percent)}%`,
        <IconChartBar size={22} />,
        null,
        <div
          className={classes.progress}
          role="progressbar"
          aria-label={t('payroll.cards.progress')}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={percent}
        >
          <div className={classes.progressBar} style={{ width: `${percent}%` }} />
        </div>,
      )}
    </div>
  );
}
